/**
 * Platform detection — "are we running inside the Capacitor native shell?"
 *
 * The native iOS/Android apps load the same SPA bundle, but the product is
 * intentionally DIFFERENT there (App Store guideline 4.2 — no repackaged
 * website): no marketing homepage, no top nav, no external signup/billing
 * links (3.1.1), bottom tab bar instead. The paid app IS the license — no
 * login wall.
 *
 * Capacitor injects `window.Capacitor` into its WebView, so no import of
 * @capacitor/core is needed here (keeps the web bundle dependency-free).
 *
 * Dev override — preview the native experience in a desktop browser:
 *   http://localhost:5173/?native=1   (persists for the tab; ?native=0 clears)
 */

/** Shape of the Preferences plugin exposed on Capacitor's runtime bridge. */
export interface CapacitorPreferencesBridge {
  get(opts: { key: string }): Promise<{ value: string | null }>;
  set(opts: { key: string; value: string }): Promise<void>;
  remove(opts: { key: string }): Promise<void>;
  /** Optional: absent on very old plugin versions, so callers must guard. */
  keys?(): Promise<{ keys: string[] }>;
}

/** Shape of the Haptics plugin exposed on Capacitor's runtime bridge. */
export interface CapacitorHapticsBridge {
  impact?(opts: { style: 'LIGHT' | 'MEDIUM' | 'HEAVY' }): Promise<void>;
  notification?(opts: {
    type: 'SUCCESS' | 'WARNING' | 'ERROR';
  }): Promise<void>;
}

/**
 * Shape of the App plugin on the runtime bridge. Android only in practice:
 * iOS has no hardware back button, so nothing subscribes there.
 */
export interface CapacitorListenerHandle {
  remove: () => void | Promise<void>;
}

export interface CapacitorAppBridge {
  /**
   * NOTE: the runtime bridge returns the handle SYNCHRONOUSLY, unlike the
   * @capacitor/app npm wrapper which returns a Promise. Callers must accept
   * both — assuming a Promise here threw "addListener(...).then is not a
   * function" at boot and blanked the whole app on device.
   */
  addListener(
    event: 'backButton',
    cb: (data: { canGoBack: boolean }) => void,
  ): CapacitorListenerHandle | Promise<CapacitorListenerHandle>;
  exitApp?(): void | Promise<void>;
}

declare global {
  interface Window {
    Capacitor?: {
      isNativePlatform?: () => boolean;
      getPlatform?: () => string;
      Plugins?: {
        Preferences?: CapacitorPreferencesBridge;
        Haptics?: CapacitorHapticsBridge;
        App?: CapacitorAppBridge;
      };
    };
  }
}

const OVERRIDE_KEY = 'de-native-preview';

function readOverride(): boolean {
  try {
    const param = new URLSearchParams(window.location.search).get('native');
    if (param === '1') sessionStorage.setItem(OVERRIDE_KEY, '1');
    if (param === '0') sessionStorage.removeItem(OVERRIDE_KEY);
    return sessionStorage.getItem(OVERRIDE_KEY) === '1';
  } catch {
    return false;
  }
}

/** True when running inside the iOS/Android Capacitor shell (or dev preview). */
export function isNativeApp(): boolean {
  if (isRealNative()) return true;
  if (import.meta.env.DEV) return readOverride();
  return false;
}

/**
 * True ONLY inside the real Capacitor shell — never in the `?native=1` dev
 * preview. Use for wiring that must differ on-device (API origin, storage);
 * use `isNativeApp()` for UI/product-surface decisions.
 */
export function isRealNative(): boolean {
  return typeof window !== 'undefined' && !!window.Capacitor?.isNativePlatform?.();
}

/** 'ios' | 'android' on device; 'web' in browsers/dev preview. */
export function nativePlatform(): string {
  return (typeof window !== 'undefined' && window.Capacitor?.getPlatform?.()) || 'web';
}

/**
 * Human platform name for "DraftEdge for X" strings. One app, two stores —
 * hardcoding "iOS" shipped that word to Android users.
 */
export function platformLabel(): string {
  const p = nativePlatform();
  if (p === 'android') return 'Android';
  if (p === 'ios') return 'iOS';
  return 'web';
}
